import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { AppHeader } from '../components/AppHeader';
import { EraseScreen } from './EraseScreen';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { LoadingView } from '../components/LoadingView';
import { Notice } from '../components/Notice';
import type { DeletedFrame, MovieFrame, PlaybackSpeed, Project } from '../domain/model';
import { MIN_EXPORT_FRAMES, movieFilename, safeMovieTitle, SPEEDS } from '../domain/model';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { usePlayback } from '../hooks/usePlayback';
import { deliverMovie } from '../services/deliverMovie';
import type { ExportResult } from '../services/exportMovie';
import { exportMovie } from '../services/exportMovie';
import { canKeepMovie, SaveMovieError, saveFinishedMovie } from '../services/savedMovies';
import { isEraserEnabled } from '../services/parentApi';
import {
  deleteFrame,
  getProject,
  moveFrame,
  getProjectFrames,
  replaceFrameImage,
  restoreFrame,
  restoreFrameImage,
  updateExportState,
  updateProjectSpeed,
} from '../storage/projectRepository';

function FrameThumb({ frame, number, selected, dragging, style, onChoose, onPointerDown }: {
  frame: MovieFrame;
  number: number;
  selected: boolean;
  dragging: boolean;
  style?: CSSProperties | undefined;
  onChoose: () => void;
  onPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
}) {
  const url = useObjectUrl(frame.blob);
  return (
    <button
      className={`frame-thumb ${selected ? 'selected' : ''} ${dragging ? 'dragging' : ''}`}
      type="button"
      style={style}
      onClick={onChoose}
      onPointerDown={onPointerDown}
      aria-label={`Picture ${number}${selected ? ', selected' : ''}`}
      aria-pressed={selected}
    >
      {url && <img src={url} alt="" />}
      <span>{number}</span>
    </button>
  );
}

const DRAG_HOLD_MS = 180;
const DRAG_START_PX = 8;
const FALLBACK_SLOT_PX = 101;

interface DragState { from: number; to: number; dx: number; slot: number }

/**
 * Touch-friendly drag-to-reorder for the thumbnail strip.
 * Touch: press and hold a picture, then drag it. A quick swipe still scrolls the strip.
 * Mouse/pen: just drag. The Move Earlier/Later buttons remain as the accessible fallback.
 */
function useDragReorder(count: number, disabled: boolean, onReorder: (from: number, to: number) => void) {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<DragState>();
  const dragRef = useRef<DragState | undefined>(undefined);
  const pendingRef = useRef<{ from: number; startX: number; startY: number; pointerType: string; timer: ReturnType<typeof setTimeout> | undefined } | undefined>(undefined);
  const suppressTapRef = useRef(false);
  const reorderRef = useRef(onReorder);
  useEffect(() => { reorderRef.current = onReorder; }, [onReorder]);

  const slotWidth = useCallback(() => {
    const first = stripRef.current?.querySelector('.frame-thumb');
    if (!first) return FALLBACK_SLOT_PX;
    const strip = stripRef.current;
    const gap = strip ? parseFloat(getComputedStyle(strip).columnGap || '') || 9 : 9;
    return first.getBoundingClientRect().width + gap;
  }, []);

  const activate = useCallback((from: number) => {
    suppressTapRef.current = true;
    const next: DragState = { from, to: from, dx: 0, slot: slotWidth() };
    dragRef.current = next;
    setDrag(next);
  }, [slotWidth]);

  useEffect(() => {
    function clearPending() {
      const pending = pendingRef.current;
      if (pending?.timer !== undefined) clearTimeout(pending.timer);
      pendingRef.current = undefined;
    }
    function onMove(event: PointerEvent) {
      const pending = pendingRef.current;
      if (!pending) return;
      const dx = event.clientX - pending.startX;
      const dy = event.clientY - pending.startY;
      if (!dragRef.current) {
        if (Math.hypot(dx, dy) < DRAG_START_PX) return;
        if (pending.pointerType === 'touch') {
          // The finger moved before the hold finished: let the strip scroll instead.
          clearPending();
          return;
        }
        activate(pending.from);
      }
      const current = dragRef.current;
      if (!current) return;
      const to = Math.min(count - 1, Math.max(0, pending.from + Math.round(dx / current.slot)));
      const next = { ...current, to, dx };
      dragRef.current = next;
      setDrag(next);
    }
    function onEnd() {
      const finished = dragRef.current;
      clearPending();
      dragRef.current = undefined;
      setDrag(undefined);
      if (finished && finished.to !== finished.from) reorderRef.current(finished.from, finished.to);
    }
    function onCancel() {
      clearPending();
      dragRef.current = undefined;
      setDrag(undefined);
    }
    function onTouchMove(event: TouchEvent) {
      if (dragRef.current) event.preventDefault();
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [count, activate]);

  const startDrag = useCallback((from: number, event: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled || count < 2) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    suppressTapRef.current = false;
    const timer = event.pointerType === 'touch'
      ? setTimeout(() => { if (pendingRef.current && !dragRef.current) activate(pendingRef.current.from); }, DRAG_HOLD_MS)
      : undefined;
    pendingRef.current = { from, startX: event.clientX, startY: event.clientY, pointerType: event.pointerType, timer };
  }, [disabled, count, activate]);

  const styleFor = useCallback((index: number): CSSProperties | undefined => {
    if (!drag) return undefined;
    if (index === drag.from) return { transform: `translateX(${drag.dx}px) scale(1.06)` };
    if (index > drag.from && index <= drag.to) return { transform: `translateX(-${drag.slot}px)` };
    if (index < drag.from && index >= drag.to) return { transform: `translateX(${drag.slot}px)` };
    return undefined;
  }, [drag]);

  const wasDrag = useCallback(() => suppressTapRef.current, []);

  return { stripRef, drag, startDrag, styleFor, wasDrag };
}

interface PreviewScreenProps {
  projectId: string;
  onHome: () => void;
  onCamera: () => void;
}

export function PreviewScreen({ projectId, onHome, onCamera }: PreviewScreenProps) {
  const [project, setProject] = useState<Project>();
  const [frames, setFrames] = useState<MovieFrame[]>();
  const [missingCount, setMissingCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<MovieFrame>();
  const [undoFrame, setUndoFrame] = useState<DeletedFrame>();
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState<'info' | 'success' | 'error'>('info');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pendingExport, setPendingExport] = useState<ExportResult>();
  const [keepRoom, setKeepRoom] = useState<'ok' | 'too-big' | 'low' | 'unknown'>('ok');
  const [finishing, setFinishing] = useState(false);
  const [eraserOn, setEraserOn] = useState(false);
  const [erasing, setErasing] = useState<MovieFrame>();
  const playback = usePlayback(frames?.length ?? 0, project ? SPEEDS[project.speed].fps : SPEEDS.normal.fps);
  const currentFrame = frames?.[playback.index];
  const currentUrl = useObjectUrl(currentFrame?.blob);

  const load = useCallback(async () => {
    let movie = await getProject(projectId);
    if (!movie) throw new Error('Movie missing');
    const wasInterrupted = movie.exportState === 'working';
    if (wasInterrupted) movie = await updateExportState(movie.id, 'failed');
    const loaded = await getProjectFrames(movie);
    setProject(movie);
    setFrames(loaded.frames);
    setMissingCount(loaded.missingCount);
    if (wasInterrupted) {
      setMessageTone('info');
      setMessage('The last export stopped early. Your pictures are still safe.');
    }
  }, [projectId]);

  useEffect(() => { void load().catch(() => { setMessageTone('error'); setMessage('This movie could not be opened.'); }); }, [load]);

  useEffect(() => {
    // Re-check when the app regains focus so the button appears right after a
    // grown-up turns the eraser on (possibly in another tab).
    const refresh = () => { void isEraserEnabled().then(setEraserOn); };
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  async function keepErased(edited: Blob) {
    if (!project || !erasing || !frames) return;
    try {
      const updated = await replaceFrameImage(project.id, erasing.id, edited);
      setFrames(frames.map((frame) => (frame.id === updated.id ? updated : frame)));
      setMessageTone('success');
      setMessage('Poof! The new picture is saved. You can always bring back the real one.');
    } catch {
      setMessageTone('error');
      setMessage('The new picture could not be saved. Your old picture is still safe.');
    } finally {
      setErasing(undefined);
    }
  }

  async function restoreErased() {
    if (!project || !erasing || !frames) return;
    try {
      const updated = await restoreFrameImage(project.id, erasing.id);
      setFrames(frames.map((frame) => (frame.id === updated.id ? updated : frame)));
      setMessageTone('success');
      setMessage('The real picture is back!');
    } catch {
      setMessageTone('error');
      setMessage('That did not work. Your picture is still safe.');
    } finally {
      setErasing(undefined);
    }
  }

  async function chooseSpeed(speed: PlaybackSpeed) {
    if (!project) return;
    playback.stop();
    setProject(await updateProjectSpeed(project.id, speed));
  }

  async function removePicture() {
    if (!project || !deleteTarget || !frames) return;
    try {
      const result = await deleteFrame(project.id, deleteTarget.id);
      setProject(result.project);
      setFrames(frames.filter((frame) => frame.id !== deleteTarget.id));
      setUndoFrame(result.deleted);
      playback.restart();
      setMessageTone('info');
      setMessage('Picture removed.');
    } catch {
      setMessageTone('error');
      setMessage('That picture could not be removed. It is still safe.');
    } finally {
      setDeleteTarget(undefined);
    }
  }

  async function undoDelete() {
    if (!project || !undoFrame || !frames) return;
    try {
      const updated = await restoreFrame(project.id, undoFrame);
      const next = [...frames];
      next.splice(Math.min(undoFrame.index, next.length), 0, undoFrame.frame);
      setProject(updated);
      setFrames(next);
      setUndoFrame(undefined);
      setMessageTone('success');
      setMessage('Picture put back!');
    } catch {
      setMessageTone('error');
      setMessage('That picture could not be put back.');
    }
  }

  async function movePicture(offset: number) {
    if (!project || !frames || !currentFrame || exporting) return;
    const from = playback.index;
    const to = from + offset;
    if (to < 0 || to >= frames.length) return;
    playback.stop();
    try {
      const updated = await moveFrame(project.id, currentFrame.id, offset);
      const next = [...frames];
      next.splice(to, 0, ...next.splice(from, 1));
      setProject(updated);
      setFrames(next);
      playback.choose(to);
      setMessageTone('success');
      setMessage(`Picture moved to spot ${to + 1}.`);
    } catch {
      setMessageTone('error');
      setMessage('That picture could not be moved. It is still safe.');
    }
  }

  const reorderPicture = useCallback(async (from: number, to: number) => {
    if (!project || !frames || exporting) return;
    const frame = frames[from];
    if (!frame || from === to || to < 0 || to >= frames.length) return;
    playback.stop();
    try {
      const updated = await moveFrame(project.id, frame.id, to - from);
      const next = [...frames];
      next.splice(to, 0, ...next.splice(from, 1));
      setProject(updated);
      setFrames(next);
      playback.choose(to);
      setMessageTone('success');
      setMessage(`Picture moved to spot ${to + 1}.`);
    } catch {
      setMessageTone('error');
      setMessage('That picture could not be moved. It is still safe.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, frames, exporting, playback.stop, playback.choose]);

  const dragReorder = useDragReorder(frames?.length ?? 0, exporting, (from, to) => { void reorderPicture(from, to); });

  async function makeMovie() {
    if (!project || !frames || frames.length < MIN_EXPORT_FRAMES || exporting) return;
    playback.stop();
    setExporting(true);
    setProgress(0);
    setMessage('');
    try {
      setProject(await updateExportState(project.id, 'working'));
      const result = await exportMovie(project, frames, (done, total) => setProgress(total ? Math.round(done / total * 100) : 0));
      setProject(await updateExportState(project.id, 'ready'));
      setKeepRoom(await canKeepMovie(result.blob.size).catch(() => 'unknown' as const));
      setPendingExport(result);
    } catch (error) {
      setProject(await updateExportState(project.id, 'failed').catch(() => project));
      setMessageTone('error');
      setMessage(error instanceof Error ? `${error.message} Your pictures are still safe.` : 'The movie could not be made. Your pictures are still safe.');
    } finally {
      setExporting(false);
    }
  }

  async function finishExport(action: 'keep' | 'share' | 'both') {
    if (!project || !frames || !pendingExport || finishing) return;
    setFinishing(true);
    try {
      let kept = false;
      if (action === 'keep' || action === 'both') {
        await saveFinishedMovie(project, pendingExport, frames[0], frames.length);
        kept = true;
      }
      let delivery: 'shared' | 'downloaded' | undefined;
      if (action === 'share' || action === 'both') {
        delivery = await deliverMovie(pendingExport.blob, movieFilename(project, pendingExport.extension));
      }
      setPendingExport(undefined);
      setMessageTone('success');
      setMessage(kept
        ? (delivery ? 'Your movie is kept in My Movies and shared!' : 'Your movie is kept in My Movies!')
        : delivery === 'shared' ? 'Your movie is ready!' : 'Your movie is ready and downloaded!');
    } catch (error) {
      setMessageTone('error');
      setMessage(error instanceof SaveMovieError
        ? error.message
        : 'That did not work. Your movie is still ready — try again.');
    } finally {
      setFinishing(false);
    }
  }

  if ((!project || !frames) && !message) return <LoadingView />;
  if (!project || !frames) return <main className="centered"><Notice tone="error" role="alert">{message}</Notice><button className="button primary" onClick={onHome}>Go home</button></main>;

  return (
    <div className="screen-shell preview-shell">
      <AppHeader title={safeMovieTitle(project.title, project.createdAt)} onHome={onHome} />
      <main className="preview-screen">
        <section className="preview-player" aria-label="Movie preview">
          {currentUrl ? <img src={currentUrl} alt={`Movie picture ${playback.index + 1} of ${frames.length}`} /> : <div className="empty-preview"><Icon name="movies" /><p>No pictures yet</p></div>}
          {frames.length > 0 && <span className="preview-count">{playback.index + 1} / {frames.length}</span>}
        </section>
        <div className="playback-controls">
          <button className="round-control" type="button" onClick={playback.restart} disabled={!frames.length} aria-label="Restart movie"><Icon name="restart" /></button>
          <button className="main-play" type="button" onClick={playback.playPause} disabled={!frames.length} aria-label={playback.playing ? 'Pause movie' : 'Play movie'}><Icon name={playback.playing ? 'pause' : 'play'} /></button>
          <button className="round-control delete-current" type="button" onClick={() => setDeleteTarget(currentFrame)} disabled={!currentFrame} aria-label="Delete this picture"><Icon name="trash" /></button>
          {eraserOn && <button className="round-control erase-button" type="button" onClick={() => { playback.stop(); setErasing(currentFrame); }} disabled={!currentFrame || exporting} aria-label="Magic Eraser for this picture"><Icon name="sparkles" /></button>}
        </div>
        <fieldset className="speed-choices">
          <legend>Movie speed</legend>
          <div>
            {(Object.keys(SPEEDS) as PlaybackSpeed[]).map((speed) => (
              <button key={speed} className={project.speed === speed ? 'selected' : ''} type="button" onClick={() => { void chooseSpeed(speed); }} aria-pressed={project.speed === speed}>
                <strong>{SPEEDS[speed].label}</strong><small>{SPEEDS[speed].fps} fps</small>
              </button>
            ))}
          </div>
        </fieldset>
        <section className="frames-section" aria-labelledby="pictures-heading">
          <div className="section-heading"><h2 id="pictures-heading">Pictures</h2><span>{frames.length}</span></div>
          <div className={`frame-strip ${dragReorder.drag ? 'reordering' : ''}`} ref={dragReorder.stripRef}>
            {/* eslint-disable-next-line react-hooks/refs -- startDrag/wasDrag only read refs when the event handlers fire, not during render */}
            {frames.map((frame, index) => (
              <FrameThumb
                key={frame.id}
                frame={frame}
                number={index + 1}
                selected={index === playback.index}
                dragging={dragReorder.drag?.from === index}
                style={dragReorder.styleFor(index)}
                onChoose={() => { if (!dragReorder.wasDrag()) playback.choose(index); }}
                onPointerDown={(event) => dragReorder.startDrag(index, event)}
              />
            ))}
          </div>
          {frames.length > 1 && <p className="drag-hint">Press and hold a picture, then drag it to a new spot.</p>}
          {frames.length > 1 && (
            <div className="move-controls">
              <button className="button secondary move-button" type="button" onClick={() => { void movePicture(-1); }} disabled={playback.index === 0 || exporting} aria-label={`Move picture ${playback.index + 1} earlier`}><Icon name="moveBack" /> Move Earlier</button>
              <button className="button secondary move-button" type="button" onClick={() => { void movePicture(1); }} disabled={playback.index >= frames.length - 1 || exporting} aria-label={`Move picture ${playback.index + 1} later`}>Move Later <Icon name="moveForward" /></button>
            </div>
          )}
        </section>
        {missingCount > 0 && <Notice tone="warning" role="alert">{missingCount} damaged {missingCount === 1 ? 'picture was' : 'pictures were'} skipped. The other pictures are safe.</Notice>}
        {message && <Notice tone={messageTone} role={messageTone === 'error' ? 'alert' : 'status'}>{message}{undoFrame && <button className="notice-action" type="button" onClick={() => { void undoDelete(); }}><Icon name="undo" /> Undo</button>}</Notice>}
        {exporting && <div className="export-progress" role="status" aria-live="polite"><label htmlFor="export-progress">Making your movie… {progress}%</label><progress id="export-progress" max="100" value={progress} /></div>}
        {frames.length < MIN_EXPORT_FRAMES && <p className="more-pictures-hint">Take one more picture before making the movie.</p>}
        <div className="preview-actions">
          <button className="button secondary" type="button" onClick={onCamera} disabled={exporting}><Icon name="camera" /> Add Pictures</button>
          <button className="button primary" type="button" onClick={() => { void makeMovie(); }} disabled={frames.length < MIN_EXPORT_FRAMES || exporting}><Icon name="sparkles" /> {exporting ? 'Making Movie…' : 'Finish & Save'}</button>
        </div>
      </main>
      {pendingExport && (
        <div className="dialog-shade" role="presentation">
          <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="keep-title">
            <h2 id="keep-title">🎬 Your movie is ready!</h2>
            <p>Keep it in My Movies to watch any time, or share it now.</p>
            {keepRoom === 'low' && <Notice tone="warning" role="status">This device does not have enough room to keep this movie. You can still share or download it, or delete an old movie in My Movies first.</Notice>}
            {keepRoom === 'too-big' && <Notice tone="warning" role="status">This movie is too big to keep in the app, but you can still share or download it.</Notice>}
            <div className="dialog-actions stacked">
              {(keepRoom === 'ok' || keepRoom === 'unknown') && (
                <>
                  <button className="button primary" type="button" disabled={finishing} onClick={() => { void finishExport('keep'); }}><Icon name="film" /> Keep in My Movies</button>
                  <button className="button secondary" type="button" disabled={finishing} onClick={() => { void finishExport('share'); }}><Icon name="share" /> Share / Download</button>
                  <button className="button secondary" type="button" disabled={finishing} onClick={() => { void finishExport('both'); }}><Icon name="sparkles" /> Do Both</button>
                </>
              )}
              {(keepRoom === 'low' || keepRoom === 'too-big') && (
                <button className="button primary" type="button" disabled={finishing} onClick={() => { void finishExport('share'); }}><Icon name="share" /> Share / Download</button>
              )}
              <button className="button quiet" type="button" disabled={finishing} onClick={() => { setPendingExport(undefined); setMessageTone('info'); setMessage('Okay! You can make the movie again any time.'); }}>Not now</button>
            </div>
          </div>
        </div>
      )}
      {erasing && <EraseScreen frame={erasing} onKeep={(edited) => { void keepErased(edited); }} onRestoreOriginal={() => { void restoreErased(); }} onClose={() => setErasing(undefined)} />}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this picture?"
        message="The other pictures will stay in the same order. You can undo this next."
        confirmLabel="Delete picture"
        dangerous
        onCancel={() => setDeleteTarget(undefined)}
        onConfirm={() => { void removePicture(); }}
      />
    </div>
  );
}
